import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { defaultCustomerConfig } from "../theme/theme";
import pkg from '../../package.json';

const APP_VERSION = pkg.version;

// ── Font loader — chunked to avoid stack-overflow on large TTF ─────────────
async function loadHeeboFont(doc) {
  try {
    const resp = await fetch('/fonts/Heebo-Regular.ttf');
    if (!resp.ok) throw new Error('font fetch failed');
    const buffer = await resp.arrayBuffer();
    const bytes  = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 8192) {
      binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
    }
    const b64 = btoa(binary);
    doc.addFileToVFS('Heebo-Regular.ttf', b64);
    doc.addFont('Heebo-Regular.ttf', 'Heebo', 'normal', 'Identity-H');
    doc.setFont('Heebo', 'normal');
    return true;
  } catch (e) {
    console.warn('[PDF] Heebo font load failed, falling back to helvetica:', e);
    doc.setFont('helvetica');
    return false;
  }
}

// ── Logical → visual order for RTL Hebrew text in jsPDF ─────────────────────
function toRTL(text) {
  if (!text) return '';
  if (!/[\u0590-\u05FF]/.test(text)) return text; // no Hebrew → return as-is
  return [...text].reverse()
    .map(c => c === '(' ? ')' : c === ')' ? '(' : c)
    .join('');              // any Hebrew → full reversal
}

// ── ISO date → DD/MM/YYYY ──────────────────────────────────────────────────
function isoToDisplay(iso) {
  if (!iso || iso.length < 10) return iso || '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

// ── Hex → [r, g, b] ────────────────────────────────────────────────────────
function hexToRgb(hex) {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

// ── Advance Y, adding a new page if needed ─────────────────────────────────
function ensureY(doc, y, needed, pageH, margin) {
  if (y + needed > pageH - margin) {
    doc.addPage();
    return margin;
  }
  return y;
}

// ── Render the form-input summary as a Hebrew text table ───────────────────
// Returns the new Y position after all sections.
function renderFormSummary(doc, formValues, config, startY, pageW, pageH, margin, usableW) {
  const fv = formValues || {};
  const primary = hexToRgb(config.primaryColor || '#1A2C5B');
  const teal    = hexToRgb(config.ctaColor    || '#00B2B2');

  const SECTION_H   = 8;   // header bar height (mm)
  const ROW_H       = 7;   // data row height (mm)
  const FONT_HEADER = 9;
  const FONT_ROW    = 9;
  const RIGHT_X     = margin + usableW;          // right edge (RTL origin)
  const VALUE_X     = margin + usableW * 0.50;   // value column right-edge

  const fundTypeLabel = { comprehensive: 'מקיפה', supplementary: 'כללית' };
  const genderLabel   = { male: 'זכר', female: 'נקבה' };

  const sections = [
    {
      color: primary,
      title: 'פרטים אישיים',
      rows: [
        fv.clientName ? ['שם העמית/ה', fv.clientName]            : null,
        fv.clientId   ? ['ת.ז.',        fv.clientId]              : null,
        ['תאריך לידה', isoToDisplay(fv.memberBirthDate)],
        ['מגדר',       genderLabel[fv.memberGender] || fv.memberGender],
      ].filter(Boolean),
    },
    {
      color: teal,
      title: 'פרטי פרישה',
      rows: [
        ['חודש / שנת פרישה', `${String(fv.retirementMonth).padStart(2, '0')}/${fv.retirementYear}`],
        ['סוג קרן',           fundTypeLabel[fv.fundType] || fv.fundType],
      ].filter(Boolean),
    },
    {
      color: primary,
      title: 'בן / בת זוג',
      rows: [
        ['בן/בת זוג', fv.hasSpouse ? 'יש' : 'אין'],
        ...(fv.hasSpouse ? [
          ['תאריך לידה בן/בת זוג',   isoToDisplay(fv.spouseBirthDate)],
          ['מגדר בן/בת זוג',         genderLabel[fv.spouseGender] || fv.spouseGender],
          ['שיעור קצבה לבן/בת זוג',  `${fv.spousePensionRate}%`],
        ] : []),
      ],
    },
    {
      color: teal,
      title: 'ילדים (עד גיל 21)',
      rows: (fv.orphans && fv.orphans.length > 0)
        ? fv.orphans.map((o, i) => [
            `ילד ${i + 1}`,
            `${isoToDisplay(o.birthdate)}, שיעור ${o.orphanRate}%`,
          ])
        : [['ילדים', 'אין']],
    },
    {
      color: primary,
      title: 'נתונים פיננסיים',
      rows: [
        ['צבירה',                `\u20AA${Number(fv.savingsAmount || 0).toLocaleString('en-US')}`],
        ['תקופת הבטחה',          Number(fv.securityPeriod) === 0 ? 'ללא הבטחה' : `${fv.securityPeriod} חודשים`],
        Number(fv.advanceMonths) > 0
          ? ['תקופת תשלום נוספת', `${fv.advanceMonths} חודשים`]
          : null,
        ['דמי ניהול',            `${fv.managementFee}%`],
        Number(fv.capitalizationYears) > 0
          ? ['היוון', `${fv.capitalizationYears} שנים, ${fv.capitalizationRate}%`]
          : null,
      ].filter(Boolean),
    },
  ];

  let y = startY;

  sections.forEach((section) => {
    // ── Section header ──
    y = ensureY(doc, y, SECTION_H + ROW_H, pageH, margin);
    const [r, g, b] = section.color;
    doc.setFillColor(r, g, b);
    doc.rect(margin, y, usableW, SECTION_H, 'F');
    doc.setFont('Heebo', 'normal');
    doc.setFontSize(FONT_HEADER);
    doc.setTextColor(255, 255, 255);
    doc.text(toRTL(section.title), RIGHT_X, y + SECTION_H - 2, { align: 'right' });
    y += SECTION_H;

    // ── Data rows ──
    doc.setFontSize(FONT_ROW);
    section.rows.forEach((row, ri) => {
      y = ensureY(doc, y, ROW_H, pageH, margin);
      // Alternating background
      if (ri % 2 === 0) {
        doc.setFillColor(248, 249, 251);
        doc.rect(margin, y, usableW, ROW_H, 'F');
      }
      const [label, value] = row;
      // Label — right-aligned on right side
      doc.setTextColor(40, 40, 40);
      doc.text(toRTL(`${label}:`), RIGHT_X, y + ROW_H - 2, { align: 'right' });
      // Value — right-aligned at mid-column separator
      doc.setTextColor(80, 80, 80);
      doc.text(toRTL(String(value ?? '')), VALUE_X, y + ROW_H - 2, { align: 'right' });
      y += ROW_H;
    });

    y += 3; // gap between sections
  });

  return y;
}

// ── Load image from URL → { dataUrl, width, height } ──────────────────────
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width  = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve({ dataUrl: canvas.toDataURL('image/png'), width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = reject;
    img.src = src;
  });
}

// ── Main PDF export ────────────────────────────────────────────────────────
export async function exportPdf({ captureRef, formValues, config = defaultCustomerConfig }) {
  const element = captureRef && captureRef.current;
  if (!element) return;

  // Capture results table upfront
  const canvas = await html2canvas(element, {
    scale: 1.5,
    useCORS: true,
    backgroundColor: '#ffffff',
    windowWidth: element.scrollWidth,
    windowHeight: element.scrollHeight,
  });
  const imgWidth  = canvas.width;
  const imgHeight = canvas.height;

  const doc     = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW   = doc.internal.pageSize.getWidth();
  const pageH   = doc.internal.pageSize.getHeight();
  const margin  = 10;
  const usableW = pageW - margin * 2;

  // ── Load Heebo font ────────────────────────────────────────────────────────
  const heeboLoaded = await loadHeeboFont(doc);
  if (heeboLoaded) doc.setFont('Heebo', 'normal');

  // ── Header banner ─────────────────────────────────────────────────────────
  let headerH = 20;
  try {
    const bannerImg = await loadImage('/logos/More/more_banner_header_pdf.png');
    headerH = Math.min(20, pageW * bannerImg.height / bannerImg.width);
    doc.addImage(bannerImg.dataUrl, 'PNG', 0, 0, pageW, headerH);
  } catch {
    const [r, g, b] = hexToRgb(config.primaryColor || '#1A2C5B');
    doc.setFillColor(r, g, b);
    doc.rect(0, 0, pageW, headerH, 'F');
  }

  let y = headerH + 4;

  // ── Generation date ────────────────────────────────────────────────────────
  if (heeboLoaded) doc.setFont('Heebo', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(80, 80, 80);
  const dateStr = new Date().toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const dateLabel = toRTL('\u05EA\u05D0\u05E8\u05D9\u05DA \u05D4\u05E4\u05E7\u05D4:');
  doc.text(dateLabel, pageW - margin, headerH + 5, { align: 'right' });
  doc.text(dateStr, pageW - margin - doc.getTextWidth(dateLabel) - 2, headerH + 5, { align: 'right' });
  const versionStr = toRTL(`גרסא: ${APP_VERSION}`);
  doc.text(versionStr, margin, headerH + 5, { align: 'left' });
  y += 6;

  // ── Form summary (Hebrew text table) ─────────────────────────────────────
  if (heeboLoaded) {
    y = renderFormSummary(doc, formValues, config, y, pageW, pageH, margin, usableW);
    y += 4;
  }

  // ── Results table (html2canvas image) ─────────────────────────────────────
  doc.addPage();
  let ry = margin;
  const mmPerPx   = usableW / imgWidth;
  const pxPerPage = Math.floor((pageH - margin * 2) / mmPerPx);
  let srcY = 0;
  while (srcY < imgHeight) {
    if (srcY > 0) { doc.addPage(); ry = margin; }
    const sliceH      = Math.min(pxPerPage, imgHeight - srcY);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width  = imgWidth;
    sliceCanvas.height = sliceH;
    sliceCanvas.getContext('2d').drawImage(canvas, 0, srcY, imgWidth, sliceH, 0, 0, imgWidth, sliceH);
    doc.addImage(sliceCanvas.toDataURL('image/jpeg', 0.82), 'JPEG', margin, ry, usableW, sliceH * mmPerPx);
    srcY += sliceH;
    ry   += sliceH * mmPerPx;
  }
  let finalY = ry + 4;

  // ── Disclaimer (doc.text — no html2canvas) ────────────────────────────────
  if (config.disclaimerText) {
    const disclaimerText = config.disclaimerText.replace(/\s*\n+\s*/g, ' ').trim();
    doc.setFontSize(8);
    const lineH  = 5;
    const lines  = doc.splitTextToSize(disclaimerText, usableW - 6);
    const blockH = lines.length * lineH + 10;
    const footerReserve = 18;
    if (finalY + blockH + footerReserve > pageH) { doc.addPage(); finalY = margin; }
    doc.setFillColor(245, 245, 245);
    doc.rect(margin, finalY, usableW, blockH, 'F');
    finalY += 6;
    doc.setTextColor(110, 112, 122);
    const rightX = pageW - margin - 3;
    lines.forEach(line => {
      doc.text(toRTL(line), rightX, finalY, { align: 'right' });
      finalY += lineH;
    });
    doc.setTextColor(0, 0, 0);
    finalY += 4;
  }

  // ── Powered-by line (doc.text — no html2canvas) ───────────────────────────
  if (heeboLoaded) doc.setFont('Heebo', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(110, 112, 122);
  const year = new Date().getFullYear();
  const poweredByText = `\u05DB\u05DC\u05D9 \u05D6\u05D4 \u05E4\u05D5\u05EA\u05D7 \u05D5\u05DE\u05D5\u05E4\u05E2\u05DC \u05E2\u05DC \u05D9\u05D3\u05D9 \u05D7\u05D1\u05E8\u05EA \u05D0\u05DE\u05E4\u05DC\u05D9\u05D9\u05E8 \u05D8\u05DB\u05E0\u05D5\u05DC\u05D5\u05D2\u05D9\u05D5\u05EA \u05D1\u05E2\u05F4\u05DE \u05E2\u05D1\u05D5\u05E8 \u05DE\u05D5\u05E8 \u05D2\u05DE\u05DC \u05D5\u05E4\u05E0\u05E1\u05D9\u05D4 \u00A9 ${year}`;
  finalY = ensureY(doc, finalY, 10, pageH, margin);
  doc.text(toRTL(poweredByText), pageW / 2, finalY + 4, { align: 'center' });
  finalY += 6;

  // ── Footer banner ──────────────────────────────────────────────────────────
  try {
    const footerImg = await loadImage('/logos/More/more_banner_footer_pdf.png');
    const footerH = pageW * footerImg.height / footerImg.width;
    doc.addImage(footerImg.dataUrl, 'PNG', 0, pageH - footerH, pageW, footerH);
  } catch { /* footer image unavailable */ }

  doc.save('pension-calculation.pdf');
}
